import React from "react";
import * as oidc from "oidc-client";
import { SessionManager } from "./SessionManager";
import { Location } from "./index";
import { Environment } from "./environments";

export const SessionContext = React.createContext<SessionState>(undefined as any);
// Legacy name
export const AuthContext = SessionContext;

export const LoggedOutReasons = {
  None: null,
  Timeout: "timeout"
};

export interface SessionState {
  error?: boolean;
  loading?: boolean;
  loggedOutReason: any;
  user?: oidc.User | null;
  logout: (reason?: any, postLogoutLocation?: Location) => void;
  login: (args?: any) => void;
  manager: SessionManager;
}

export interface SessionProviderProps {
  environment: Environment;
  clientId: string;
  scope: string;
  // Provide an implementation that uses either the loc parameter or the full path string parameter
  navigate: (loc: Location, fullPath: string) => void;
  localSessionTimeout?: number;
  autoLogin?: boolean;
  children: React.ReactNode | ((sessionStatus: SessionState) => React.ReactNode);
  onUserUpdated?: (user: oidc.User | null) => void;
  basePath?: string;
  silentRedirectUri?: string;
  automaticSilentRenew?: boolean;
}

export interface SessionProviderState {
  user?: oidc.User | null;
  initialized?: boolean;
  error?: boolean;
  loggingOut?: boolean;
  loggedOutReason: any;
}

export class SessionProvider extends React.Component<SessionProviderProps, SessionProviderState> {
  private lastUserInteraction: number = Date.now();
  private sessionManager: SessionManager;

  constructor(props: SessionProviderProps) {
    super(props);
    this.state = { loggedOutReason: LoggedOutReasons.None };
    this.sessionManager = new SessionManager({
      clientId: props.clientId,
      environment: props.environment,
      scope: props.scope,
      basePath: props.basePath,
      automaticSilentRenew: props.automaticSilentRenew,
      navigate: props.navigate
    });
  }

  public render() {
    const value = {
      error: this.state.error,
      loading: !this.state.initialized || this.state.loggingOut,
      loggedOutReason: this.state.loggedOutReason,
      user: this.state.user,
      logout: this.handleLogoutRequest,
      login: this.handleLoginRequest,
      manager: this.sessionManager
    };
    const { children } = this.props;
    return (
      <AuthContext.Provider value={value}>
        {typeof children === "function" ? (children as any)(value) : children}
      </AuthContext.Provider>
    );
  }

  public async componentDidMount() {
    // Give the app some heavy feeling :)
    await new Promise(resolve => setTimeout(resolve, 500));

    // Wire up local timeout check
    this.lastUserInteraction = Date.now();
    window.addEventListener("mousemove", this.handleUserInteraction);
    window.addEventListener("touchstart", this.handleUserInteraction);
    window.setInterval(this.handleSessionTimeoutCheck, 10000);

    // Sign up for user changed
    this.sessionManager.addUserChangedListener(this.handleUserChanged);

    try {
      await this.sessionManager.initialize();
    } catch (error) {
      console.error("Failed to initialize session manager: ", error);
      this.setState({ error: true });
    }

    if (!this.sessionManager.getUser()) {
      if (this.props.autoLogin && !this.sessionManager.getLoggedOutReason()) {
        try {
          await this.sessionManager.login();
        } catch (error) {
          console.error("Failed to login.", error);
          this.setState({ error: true, initialized: true });
        }
      } else {
        this.setState({
          initialized: true,
          loggedOutReason: this.sessionManager.getLoggedOutReason()
        });
      }
    } else {
      await this.updateUser();
    }
  }

  private handleUserInteraction = () => (this.lastUserInteraction = Date.now());

  private handleSessionTimeoutCheck = () => {
    // default to not use local session timeout
    if (!this.props.localSessionTimeout) {
      return;
    }

    if (
      this.state.user &&
      this.lastUserInteraction < Date.now() - this.props.localSessionTimeout * 1000
    ) {
      this.setState({ loggingOut: true });
      this.sessionManager.logout(
        {
          hash: window.location.hash,
          pathname: window.location.pathname,
          search: window.location.search,
          state: window.history.state
        },
        LoggedOutReasons.Timeout
      );
    }
  };

  private handleUserChanged = async () => {
    await this.updateUser();
  };

  private handleLogoutRequest = async (reason?: any, postLogoutLocation?: Location) => {
    this.setState({ loggingOut: true });

    try {
      await this.sessionManager.logout(postLogoutLocation, reason || LoggedOutReasons.None);
    } catch (error) {
      console.error("Failed to start logout: ", error);
    }
  };

  private handleLoginRequest = async (args?: any) => {
    try {
      await this.sessionManager.login(args);
    } catch (error) {
      console.error("Failed to start login: ", error);
      this.setState({ error: true });
    }
  };

  private updateUser = async () => {
    try {
      const newUser = this.sessionManager.getUser();
      this.lastUserInteraction = Date.now();
      if (this.props.onUserUpdated) {
        this.props.onUserUpdated(newUser);
      }
      this.setState({ user: newUser, initialized: true });
    } catch (error) {
      console.error("Failed to setup user context.", error);
      this.setState({ error: true, initialized: true });
      await this.sessionManager.dropUser();
    }
  };
}
